import type { Hotel } from '@/types/travel';
import { fetchWithRetry } from './request-manager';

/**
 * VOY-466: Extract Match-My-Taste fields from a raw backend hotel
 * object into a partial Hotel shape. Each field is conditionally
 * present so it doesn't overwrite undefined-with-undefined when the
 * backend omits them (personalize=false, or anonymous user). Used by
 * both mapToHotel closures below.
 */
function extractMatchFields(hotel: any): Partial<Hotel> {
  return {
    ...(typeof hotel?.match_score === 'number' ? { match_score: hotel.match_score } : {}),
    ...(Array.isArray(hotel?.match_reasons) ? { match_reasons: hotel.match_reasons } : {}),
    ...(hotel?.excluded === true ? { excluded: true } : {}),
    ...(typeof hotel?.exclusion_reason === 'string' ? { exclusion_reason: hotel.exclusion_reason } : {}),
  };
}

// VOY-333: Retry state for UI feedback
export interface RetryState {
  isRetrying: boolean;
  attempt: number;
  maxRetries: number;
  isConnectionError: boolean;
}

// VOY-333: Callback type for retry notifications
export type OnRetryCallback = (state: RetryState) => void;

// Backend URL - read from env in prod; falls back to same-origin
const BACKEND_URL = (import.meta as any).env?.VITE_API_URL || '';
const USE_HOTEL_MOCK = (import.meta as any).env?.VITE_USE_HOTEL_MOCK === 'true';

export interface HotelSearchParams {
  destination: string;
  checkin: string;
  checkout: string;
  guests: Array<{ adults: number; children: number[] }>;
  page?: number;
  per_page?: number;
  // VOY-466: 'match' and 'blend' join the sort vocabulary when
  // personalize=true. Backend default under personalize is 'blend'
  // (0.6·match + 0.4·price_inverse); explicit values override.
  // 'price' here is the personalization-mode alias the backend uses
  // (see applyPersonalization.readSortMode) — it preserves supplier
  // price-ascending order without re-ranking by match. Equivalent
  // to 'price_asc' in the non-personalized path, kept distinct so
  // the backend can disambiguate "user explicitly opted out of
  // ranking" from "default price sort".
  sort?: 'price_asc' | 'price_desc' | 'stars_desc' | 'rating_desc' | 'match' | 'price' | 'blend';
  filters?: { stars?: number[]; price_min?: number; price_max?: number };
  /**
   * VOY-466: Match-My-Taste flag. When true AND the caller is
   * authed, the backend assembles `/agent-context` and attaches
   * `match_score` + `match_reasons` to each hotel, then re-sorts
   * per `sort`. Anonymous callers: backend silently no-ops; UI
   * toggle becomes a "Sign in to personalize" affordance instead.
   */
  personalize?: boolean;
}

export interface HotelRoomOption {
  id: string;
  name: string;
  description?: string;
  price: number;
  originalPrice?: number;
  currency: string;
  cancellationPolicy?: string;
  amenities?: string[];
  images?: string[];
  maxOccupancy?: number;
  bedConfiguration?: string;
  size?: string;
  view?: string;
  available?: number;
}

export interface HotelDetailsResponse extends Hotel {
  rooms?: HotelRoomOption[];
  policies?: {
    checkIn?: string;
    checkOut?: string;
    cancellation?: string;
    childPolicy?: string;
    petPolicy?: string;
  };
  reviews?: {
    average: number;
    total: number;
    distribution?: Record<number, number>;
  };
}

export class HotelsAPIError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public details?: unknown
  ) {
    super(message);
    this.name = 'HotelsAPIError';
  }
}

// Mock hotel data for fallback
const mockHotels: Hotel[] = [
  {
    id: '1',
    name: 'Grand Plaza Hotel',
    location: 'Downtown',
    price: { amount: 250, currency: 'USD' },
    pricePerNight: 250,
    rating: 4.5,
    starRating: 4,
    amenities: ['WiFi', 'Pool', 'Spa', 'Gym', 'Restaurant', 'Bar'],
    images: [
      'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80',
      'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800&q=80',
    ],
    description: 'Experience luxury and comfort in the heart of the city. Our hotel offers premium amenities and exceptional service.',
  },
  {
    id: '2',
    name: 'Seaside Resort',
    location: 'Beachfront',
    price: { amount: 350, currency: 'USD' },
    pricePerNight: 350,
    rating: 4.8,
    starRating: 5,
    amenities: ['Beach Access', 'Pool', 'Spa', 'Multiple Restaurants', 'Water Sports', 'Kids Club'],
    images: [
      'https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?w=800&q=80',
      'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800&q=80',
    ],
    description: 'A tropical paradise with stunning ocean views and direct beach access. Perfect for a relaxing getaway.',
  },
  {
    id: '3',
    name: 'Business Inn Express',
    location: 'Business District',
    price: { amount: 180, currency: 'USD' },
    pricePerNight: 180,
    rating: 4.2,
    starRating: 3,
    amenities: ['WiFi', 'Business Center', 'Meeting Rooms', 'Gym', 'Airport Shuttle'],
    images: [
      'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800&q=80',
    ],
    description: 'Convenient location for business travelers with modern amenities and meeting facilities.',
  },
];

export interface RegionSuggestion {
  id: string | number;
  name: string;
  country_code?: string;
  lat?: number;
  lon?: number;
  type?: string;
}

export interface HotelSearchExtendedResponse {
  hotels: Hotel[];
  suggestions?: RegionSuggestion[];
  widened?: Hotel[];
  searchId?: string;
  meta?: { total?: number; page?: number; per_page?: number; total_pages?: number; jobId?: string; moreCoordinatesPending?: boolean };
}

export async function fetchCoordinatesForHotels(ids: string[]): Promise<Hotel[]> {
  if (!ids || ids.length === 0) return [];
  // VOY-36: Use fetchWithRetry for automatic retry on transient errors
  const response = await fetchWithRetry(
    `${BACKEND_URL}/api/hotels/coords/get`,
    {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ ids }),
      abortKey: `coords-${ids.join(',')}`,
    }
  );
  if (!response.ok) return [];
  const data = await response.json();
  const hotels = (data?.data?.hotels || []) as any[];
  return hotels.map((h:any) => ({
    id: h.id,
    name: h.name,
    location: h.location?.address || 'Unknown',
    coordinates: h.location ? { latitude: h.location.latitude, longitude: h.location.longitude, address: h.location.address } : null,
    price: { amount: null as any, currency: 'USD' },
    pricePerNight: null as any,
    rating: h.rating || h.starRating || 4,
    starRating: h.starRating || 4,
    amenities: h.amenities || [],
  } as Hotel));
}

export async function searchHotelsForMap(params: HotelSearchParams): Promise<HotelSearchExtendedResponse> {
  try {
    // Validate parameters
    if (!params.destination?.trim()) {
      throw new HotelsAPIError('Destination is required');
    }

    if (!params.checkin || !params.checkout) {
      throw new HotelsAPIError('Check-in and check-out dates are required');
    }

    const checkinDate = new Date(params.checkin);
    const checkoutDate = new Date(params.checkout);

    if (checkinDate >= checkoutDate) {
      throw new HotelsAPIError('Check-out date must be after check-in date');
    }

    if (checkinDate < new Date()) {
      throw new HotelsAPIError('Check-in date cannot be in the past');
    }

    if (!params.guests || params.guests.length === 0) {
      throw new HotelsAPIError('At least one guest is required');
    }

    console.log('Searching hotels for map with params:', params);

    // Use the map search endpoint that includes coordinates
    try {
      // VOY-36: Use fetchWithRetry with abort support
      const response = await fetchWithRetry(
        `${BACKEND_URL}/api/hotels/search/map`,
        {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(typeof (import.meta as any).env?.VITE_API_TOKEN === 'string' && (import.meta as any).env.VITE_API_TOKEN
            ? { Authorization: `Bearer ${(import.meta as any).env.VITE_API_TOKEN}` }
            : {}),
        },
        credentials: 'include',
        body: JSON.stringify(params),
          abortKey: `hotel-map-${params.destination}-${params.checkin}`,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        console.error('Backend returned error:', data);
        throw new Error(data.error || 'Backend request failed');
      }

      console.log('Backend returned map data:', data);

      // Handle response structure from map endpoint
      const hotels = data.data?.hotels || [];
      const meta = data.data?.meta || data.meta;

      const mapToHotel = (hotel: any): Hotel => {
        const priceAmount =
          typeof hotel?.price?.amount === 'number' ? hotel.price.amount
          : typeof hotel?.price === 'number' ? hotel.price
          : typeof hotel?.min_price === 'number' ? hotel.min_price
          : null;
        const priceEstimated = hotel?.price?.estimated === true;

        const rawImages: string[] =
          Array.isArray(hotel?.images) && hotel.images.length > 0
            ? hotel.images
            : Array.isArray(hotel?.images_ext)
              ? hotel.images_ext
                  .map((img: any) => (typeof img === 'string' ? img : img?.url))
                  .filter(Boolean)
              : Array.isArray(hotel?.photos)
                ? hotel.photos
                : [];

        const resolvedImages = rawImages
          .map((u: string) => {
            if (typeof u !== 'string') return null;
            // Replace common placeholder patterns
            const resolved = u
              .replace('{size}', '1024x768')
              .replace('{width}', '1024')
              .replace('{height}', '768');
            
            // Ensure valid URL format
            if (!resolved.startsWith('http')) {
              console.warn('Invalid image URL detected:', resolved);
              return null;
            }
            return resolved;
          })
          .filter(Boolean);

        // Derive a richer fallback description when backend doesn't provide one
        const rawDesc = typeof hotel.description === 'string' ? hotel.description.trim() : '';
        const isAutoMarker = rawDesc.toLowerCase().includes('found via autocomplete');
        const star = hotel.star_rating || hotel.stars || hotel.rating;
        const locText = (hotel.location?.address || hotel.location || hotel.address || '').toString().trim();
        const amenList: string[] = (hotel.amenities || hotel.facilities || []).filter(Boolean);
        const topAmenities = amenList.slice(0, 3).join(', ');
        let derivedDescription = '';
        if (locText) {
          derivedDescription = `Located in ${locText}${star ? ` • ${star}-star` : ''}`;
        } else if (star && topAmenities) {
          derivedDescription = `${star}-star • ${topAmenities}`;
        } else if (star) {
          derivedDescription = `${star}-star`;
        } else if (topAmenities) {
          derivedDescription = `Amenities: ${topAmenities}`;
        }

        // Debug coordinates
        const hasCoordinates = hotel.location && hotel.location.latitude && hotel.location.longitude;
        console.log('Hotel coordinates debug:', {
          id: hotel.id || hotel.hotel_id,
          name: hotel.name || hotel.hotel_name,
          location: hotel.location,
          hasCoordinates,
          latitude: hotel.location?.latitude,
          longitude: hotel.location?.longitude
        });

        const rawLoc = hotel.location?.address || hotel.location || hotel.address;
        const locationStr = typeof rawLoc === 'string'
          ? rawLoc
          : (rawLoc?.city || rawLoc?.name || '').toString();
        return {
          id: hotel.id || hotel.hotel_id,
          name: hotel.name || hotel.hotel_name,
          location: locationStr && typeof locationStr === 'string' ? locationStr : 'Unknown',
          // Include coordinates for map display
          coordinates: hasCoordinates ? {
            latitude: hotel.location.latitude,
            longitude: hotel.location.longitude,
            address: hotel.location.address
          } : null,
          price: { amount: priceAmount, currency: 'USD' },
          pricePerNight: typeof hotel?.pricePerNight === 'number' ? hotel.pricePerNight : priceAmount,
          priceEstimated,
          rating: hotel.rating || hotel.stars || 4.0,
          starRating: hotel.starRating || hotel.stars || 4,
          amenities: (hotel.amenities || hotel.facilities || ['WiFi']).slice(0, 8),
          images: resolvedImages,
          description: isAutoMarker
            ? undefined
            : (rawDesc || (derivedDescription || undefined)),
          // VOY-466: preserve Match-My-Taste fields from the backend
          // response (see `extractMatchFields` above).
          ...extractMatchFields(hotel),
        } as Hotel;
      };

      const mappedHotels: Hotel[] = Array.isArray(hotels) ? hotels.map(mapToHotel) : [];
      
      // Debug final result
      const hotelsWithCoords = mappedHotels.filter(h => h.coordinates);
      console.log('Final mapped hotels:', {
        total: mappedHotels.length,
        withCoordinates: hotelsWithCoords.length,
        firstHotel: mappedHotels[0]
      });

      return { hotels: mappedHotels, meta };
    } catch (backendError: any) {
      // Ignore AbortError - it's expected when canceling previous searches
      if (backendError?.name === 'AbortError' || backendError?.message?.includes('aborted')) {
        return { hotels: [] };
      }
      console.error('Backend error from /api/hotels/search/map:', backendError);
      // Fall back to regular search if map search fails
      return searchHotelsDirectly(params);
    }
  } catch (error: any) {
    if (error instanceof HotelsAPIError) {
      throw error;
    }

    // Ignore AbortError - it's expected when canceling previous searches
    if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
      return { hotels: [] };
    }

    console.error('Hotel map search error:', error);
    // Fall back to regular search
    return searchHotelsDirectly(params);
  }
}

// Fetch cached coordinates for a set of hotel ids (backend background enrichment fills cache)
export async function fetchHotelCoordinates(ids: string[]): Promise<Record<string, { latitude: number; longitude: number; address?: string }>> {
  if (!ids || ids.length === 0) return {};
  try {
    // VOY-36: Use fetchWithRetry
    const response = await fetchWithRetry(
      `${BACKEND_URL}/api/hotels/coords/get`,
      {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
        abortKey: `hotel-coords-${ids.slice(0, 3).join(',')}`,
      }
    );
    if (!response.ok) return {};
    const data = await response.json();
    const map: Record<string, { latitude: number; longitude: number; address?: string }> = {};
    const hotels = data?.data?.hotels || [];
    for (const h of hotels) {
      const id = String(h.id);
      if (h.location?.latitude && h.location?.longitude) {
        map[id] = { latitude: h.location.latitude, longitude: h.location.longitude, address: h.location.address };
      }
    }
    return map;
  } catch {
    return {};
  }
}

export async function searchHotelsDirectly(
  params: HotelSearchParams,
  onRetry?: OnRetryCallback
): Promise<HotelSearchExtendedResponse> {
  try {
    // Validate parameters
    if (!params.destination?.trim()) {
      throw new HotelsAPIError('Destination is required');
    }

    if (!params.checkin || !params.checkout) {
      throw new HotelsAPIError('Check-in and check-out dates are required');
    }

    const checkinDate = new Date(params.checkin);
    const checkoutDate = new Date(params.checkout);

    if (checkinDate >= checkoutDate) {
      throw new HotelsAPIError('Check-out date must be after check-in date');
    }

    if (checkinDate < new Date()) {
      throw new HotelsAPIError('Check-in date cannot be in the past');
    }

    if (!params.guests || params.guests.length === 0) {
      throw new HotelsAPIError('At least one guest is required');
    }

    console.log('Searching hotels with params:', params);

    // Try the real backend using the destination search endpoint (has images, prices, and filter support)
    try {
      // VOY-36: Use fetchWithRetry with abort support
      // VOY-333: Add onRetry callback for UI feedback during connection errors
      const response = await fetchWithRetry(
        `${BACKEND_URL}/api/hotels/search/destination`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...(typeof (import.meta as any).env?.VITE_API_TOKEN === 'string' && (import.meta as any).env.VITE_API_TOKEN
              ? { Authorization: `Bearer ${(import.meta as any).env.VITE_API_TOKEN}` }
              : {}),
          },
          credentials: 'include',
          body: JSON.stringify(params),
          abortKey: `hotel-search-${params.destination}-${params.checkin}-${params.page || 1}`,
          // VOY-333: Pass retry callback for UI feedback
          onRetry: onRetry
            ? (attempt, maxRetries, _error, isConnectionError) => {
                onRetry({
                  isRetrying: true,
                  attempt,
                  maxRetries,
                  isConnectionError,
                });
              }
            : undefined,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        console.error('Backend returned error:', data);
        throw new Error(data.error || 'Backend request failed');
      }

      console.log('Backend returned data:', data);
      console.log('Hotels count:', data.data?.hotels?.length || 0);

      // Handle extended response structure (backend wraps in data: { hotels, meta })
      const hotels = data.data?.hotels || [];
      const suggestionsRaw = data.data?.suggestions || data.suggestions || [];
      const widenedRaw = data.data?.widened || data.widened || [];
      const searchId = data.data?.search_id || data.search_id;
      const meta = data.data?.meta || data.meta;

      const mapToHotel = (hotel: any): Hotel => {
        const priceAmount =
          typeof hotel?.price?.amount === 'number' ? hotel.price.amount
          : typeof hotel?.price === 'number' ? hotel.price
          : typeof hotel?.min_price === 'number' ? hotel.min_price
          : null;
        const priceEstimated = hotel?.price?.estimated === true;

        const rawImages: string[] =
          Array.isArray(hotel?.images) && hotel.images.length > 0
            ? hotel.images
            : Array.isArray(hotel?.images_ext)
              ? hotel.images_ext
                  .map((img: any) => (typeof img === 'string' ? img : img?.url))
                  .filter(Boolean)
              : Array.isArray(hotel?.photos)
                ? hotel.photos
                : [];

        const resolvedImages = rawImages
          .map((u: string) => {
            if (typeof u !== 'string') return null;
            // Replace common placeholder patterns
            const resolved = u
              .replace('{size}', '1024x768')
              .replace('{width}', '1024')
              .replace('{height}', '768');
            
            // Ensure valid URL format
            if (!resolved.startsWith('http')) {
              console.warn('Invalid image URL detected:', resolved);
              return null;
            }
            return resolved;
          })
          .filter(Boolean);

        // Derive a richer fallback description when backend doesn't provide one
        const rawDesc = typeof hotel.description === 'string' ? hotel.description.trim() : '';
        const isAutoMarker = rawDesc.toLowerCase().includes('found via autocomplete');
        const star = hotel.star_rating || hotel.stars || hotel.rating;
        const locText = (hotel.location?.address || hotel.location || hotel.address || '').toString().trim();
        const amenList: string[] = (hotel.amenities || hotel.facilities || []).filter(Boolean);
        const topAmenities = amenList.slice(0, 3).join(', ');
        let derivedDescription = '';
        if (locText) {
          derivedDescription = `Located in ${locText}${star ? ` • ${star}-star` : ''}`;
        } else if (star && topAmenities) {
          derivedDescription = `${star}-star • ${topAmenities}`;
        } else if (star) {
          derivedDescription = `${star}-star`;
        } else if (topAmenities) {
          derivedDescription = `Amenities: ${topAmenities}`;
        }

        const rawLoc = hotel.location?.address || hotel.location || hotel.address;
        const locationStr = typeof rawLoc === 'string'
          ? rawLoc
          : (rawLoc?.city || rawLoc?.name || '').toString();
        
        // Extract coordinates for map display (same logic as searchHotelsForMap)
        const hasCoordinates = hotel.location && 
                               typeof hotel.location.latitude === 'number' && 
                               typeof hotel.location.longitude === 'number' &&
                               !isNaN(hotel.location.latitude) && 
                               !isNaN(hotel.location.longitude) &&
                               hotel.location.latitude !== 0 && 
                               hotel.location.longitude !== 0;
        
        // Also check coordinates field as fallback
        const hasCoordinatesField = hotel.coordinates && 
                                   typeof hotel.coordinates.latitude === 'number' &&
                                   typeof hotel.coordinates.longitude === 'number';
        
        const finalHasCoordinates = hasCoordinates || hasCoordinatesField;
        const coordsSource = hasCoordinates ? hotel.location : (hasCoordinatesField ? hotel.coordinates : null);
        
        return {
          id: hotel.id || hotel.hotel_id,
          name: hotel.name || hotel.hotel_name,
          location: locationStr && typeof locationStr === 'string' ? locationStr : 'Unknown',
          // Include coordinates for map display (same as searchHotelsForMap)
          coordinates: finalHasCoordinates && coordsSource ? {
            latitude: coordsSource.latitude,
            longitude: coordsSource.longitude,
            address: coordsSource.address || locationStr
          } : null,
          price: { amount: priceAmount, currency: 'USD' },
          pricePerNight: typeof hotel?.pricePerNight === 'number' ? hotel.pricePerNight : priceAmount,
          priceEstimated,
          rating: hotel.rating || hotel.stars || 4.0,
          starRating: hotel.starRating || hotel.stars || 4,
          amenities: (hotel.amenities || hotel.facilities || ['WiFi']).slice(0, 8),
          images: resolvedImages,
          description: isAutoMarker
            ? undefined
            : (rawDesc || (derivedDescription || undefined)),
          // VOY-466: preserve Match-My-Taste fields from the backend
          // response (see `extractMatchFields` above).
          ...extractMatchFields(hotel),
        } as Hotel;
      };

      const mappedHotels: Hotel[] = Array.isArray(hotels) ? hotels.map(mapToHotel) : [];
      const mappedWidened: Hotel[] = Array.isArray(widenedRaw) ? widenedRaw.map(mapToHotel) : [];
      const suggestions: RegionSuggestion[] = Array.isArray(suggestionsRaw) ? suggestionsRaw.map((s: any) => ({
        id: s?.id,
        name: s?.name,
        country_code: s?.country_code,
        lat: typeof s?.lat === 'number' ? s.lat : undefined,
        lon: typeof s?.lon === 'number' ? s.lon : undefined,
        type: s?.type
      })) : [];

      return { hotels: mappedHotels, suggestions, widened: mappedWidened, searchId, meta };
    } catch (backendError: any) {
      // Ignore AbortError - it's expected when canceling previous searches
      if (backendError?.name === 'AbortError' || backendError?.message?.includes('aborted')) {
        return { hotels: [] };
      }
      console.error('Backend error from /api/hotels/search/destination:', backendError);
      // Optional mock fallback (disabled by default)
      if (USE_HOTEL_MOCK) {
        return { hotels: getMockHotels(params) };
      }
      return { hotels: [] };
    }
  } catch (error: any) {
    if (error instanceof HotelsAPIError) {
      throw error;
    }

    // Ignore AbortError - it's expected when canceling previous searches
    if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
      return { hotels: [] };
    }

    console.error('Hotel search error:', error);
    // Optional mock fallback (disabled by default)
    return USE_HOTEL_MOCK ? { hotels: getMockHotels(params) } : { hotels: [] };
  }
}

function getMockHotels(params: HotelSearchParams): Hotel[] {
  console.log('Using mock hotel data for:', params.destination);

  // Filter mock hotels based on destination
  let filteredHotels = [...mockHotels];

  // Adjust based on destination
  const destination = params.destination.toLowerCase();
  if (destination.includes('beach') || destination.includes('miami') || destination.includes('cancun')) {
    filteredHotels = filteredHotels.filter(h => h.location.toLowerCase().includes('beach') || (h.starRating && h.starRating >= 4));
  } else if (destination.includes('business') || destination.includes('york')) {
    filteredHotels = filteredHotels.filter(h => h.location.toLowerCase().includes('business') || h.location.toLowerCase().includes('downtown'));
  }

  // Adjust prices based on dates (weekend pricing)
  const checkinDate = new Date(params.checkin);
  const isWeekend = checkinDate.getDay() === 0 || checkinDate.getDay() === 6;

  return filteredHotels.map(hotel => ({
    ...hotel,
    price: {
      amount: isWeekend ? Math.round((hotel.price as any).amount * 1.2) : (hotel.price as any).amount,
      currency: (hotel.price as any).currency || 'USD'
    },
    pricePerNight: isWeekend ? Math.round(hotel.pricePerNight * 1.2) : hotel.pricePerNight,
    location: `${hotel.location}, ${params.destination}`,
  }));
}

// Unused or unreferenced check for delete
export async function getHotelDetails(
  hotelId: string,
  params: Omit<HotelSearchParams, 'destination'>
): Promise<HotelDetailsResponse> {
  try {
    // Validate parameters
    if (!hotelId?.trim()) {
      throw new HotelsAPIError('Hotel ID is required');
    }

    if (!params.checkin || !params.checkout) {
      throw new HotelsAPIError('Check-in and check-out dates are required');
    }

    // Try backend first
    try {
      const response = await fetch(`${BACKEND_URL}/api/hotels/details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ hotelId, ...params }),
      });

      if (response.ok) {
        const data = await response.json();
        return data.hotel;
      }
    } catch {
      // Fall back to mock
    }

    // Mock response
    const mockHotel = mockHotels.find(h => h.id === hotelId);
    if (!mockHotel) {
      throw new HotelsAPIError('Hotel not found');
    }

    return {
      ...mockHotel,
          rooms: [
        {
          id: `${hotelId}-r1`,
          name: 'Standard Room',
          description: 'Comfortable room with modern amenities',
              price: (mockHotel.price as any).amount,
          currency: 'USD',
          amenities: ['WiFi', 'TV', 'Mini Bar'],
          maxOccupancy: 2,
        }
      ],
      policies: {
        checkIn: '3:00 PM',
        checkOut: '11:00 AM',
        cancellation: 'Free cancellation up to 24 hours before check-in',
      },
      reviews: {
        average: mockHotel.rating,
        total: 125,
      }
    };
  } catch (error) {
    if (error instanceof HotelsAPIError) {
      throw error;
    }

    console.error('Hotel details error:', error);
    throw new HotelsAPIError(
      'An unexpected error occurred while fetching hotel details',
      undefined,
      error
    );
  }
}

// Helper function to format guest configuration for API
// Unused or unreferenced check for delete
export function formatGuestConfiguration(
  adults: number,
  children: number[] = []
): HotelSearchParams['guests'] {
  return [{
    adults,
    children: children.map(age => Number(age))
  }];
}

// Helper function to calculate total nights
// Unused or unreferenced check for delete
export function calculateNights(checkin: string, checkout: string): number {
  const start = new Date(checkin);
  const end = new Date(checkout);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

// Helper function to format price with currency
// Unused or unreferenced check for delete
export function formatHotelPrice(price: number, currency = 'USD', nights?: number): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  });

  if (nights && nights > 1) {
    return `${formatter.format(price)} x ${nights} nights = ${formatter.format(price * nights)}`;
  }

  return formatter.format(price);
}